import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Siren, Plus, Loader2 } from "lucide-react";

const SEVERITIES = ["low", "medium", "high", "critical"];

const STATUS_STYLES = {
  open: "text-amber-600 border-amber-500/40 bg-amber-500/10",
  investigating: "text-sky-600 border-sky-500/40 bg-sky-500/10",
  resolved: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
};
const SEVERITY_STYLES = {
  low: "text-muted-foreground border-border",
  medium: "text-amber-600 border-amber-500/40",
  high: "text-orange-600 border-orange-500/40",
  critical: "text-red-600 border-red-500/40",
};

export default function PortalIncidents() {
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", severity: "medium" });

  const load = () => api.get("/employee-portal/incidents").then((r) => setItems(r.data.incidents));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.description) return;
    setSaving(true);
    try {
      await api.post("/employee-portal/incidents", form);
      toast({ title: "Incident reported" });
      setOpen(false);
      setForm({ title: "", description: "", severity: "medium" });
      load();
    } finally { setSaving(false); }
  };

  if (!items) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="portal-incidents">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Report an Incident</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="destructive" className="gap-1.5" data-testid="portal-new-incident-btn"><Plus className="w-3.5 h-3.5" /> Report incident</Button>
          </DialogTrigger>
          <DialogContent data-testid="portal-incident-dialog">
            <DialogHeader><DialogTitle>Report a security/operational incident</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="portal-incident-title-input" />
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="portal-incident-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>What happened?</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="portal-incident-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} data-testid="portal-incident-submit-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Submit report"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {items.map((v) => (
          <Card key={v.id} data-testid={`portal-incident-row-${v.id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <Siren className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium">{v.title}</div>
                  <div className="text-[12px] text-muted-foreground">{v.description}</div>
                  <Badge variant="outline" className={`mt-1 text-[10px] capitalize ${SEVERITY_STYLES[v.severity] || ""}`}>{v.severity}</Badge>
                </div>
              </div>
              <Badge variant="outline" className={`flex-shrink-0 capitalize ${STATUS_STYLES[v.status] || ""}`}>{v.status}</Badge>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-[13px] text-muted-foreground">No incidents reported. Good news!</p>}
      </div>
    </div>
  );
}
